import { useState } from 'react';
import type { Database } from '@tallypg/contracts';
import { api, formatDate, useApi } from '../api';
import { Modal } from '../components';

interface Move {
  id: string;
  fromRegionLabel: string;
  toRegionLabel: string;
  status: 'requested' | 'running' | 'succeeded' | 'failed' | 'cancelled';
  error: string | null;
  createdAt: string;
  finishedAt: string | null;
}

/**
 * Where the database lives, and the "move it" flow. With one live location the button explains that more are
 * coming; with several it opens a chooser and queues the move. Nothing changes for the customer's apps: the
 * connection string is identical in every location.
 */
export function LocationCard({ database: d, onChanged }: { database: Database; onChanged: () => void }) {
  const regions = useApi<{
    regions: { id: string; label: string }[];
    planned: { id: string; label: string }[];
  }>('/regions');
  const moves = useApi<{ moves: Move[] }>(`/databases/${d.id}/moves`);
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const all = regions.data?.regions ?? [];
  const here = all.find((r) => r.id === d.region);
  const others = all.filter((r) => r.id !== d.region);
  const active = moves.data?.moves.find((m) => m.status === 'requested' || m.status === 'running') ?? null;
  const last = moves.data?.moves[0] ?? null;

  async function submit() {
    if (!target) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/databases/${d.id}/move`, { method: 'POST', json: { region: target } });
      setOpen(false);
      setTarget(null);
      await moves.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed');
    } finally {
      setBusy(false);
    }
  }
  async function cancel(id: string) {
    setBusy(true);
    try {
      await api(`/databases/${d.id}/moves/${id}`, { method: 'DELETE' });
      await moves.reload();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <div className="row between">
        <div>
          <h2 style={{ marginBottom: 4 }}>Location</h2>
          <div style={{ fontSize: 15 }}>
            <strong>{here?.label ?? d.region}</strong>
          </div>
          <p className="muted" style={{ marginTop: 6, marginBottom: 0 }}>
            Moving is free, takes minutes, and keeps your connection string. Apps reconnect once at the
            switch, usually in under a second.
          </p>
          <p className="muted" style={{ marginTop: 6, marginBottom: 0 }}>
            Backups: nightly, kept 14 days here and 35 days in a second location; point-in-time recovery on
            request.{' '}
            {d.lastBackupAt ? `Last backup ${formatDate(d.lastBackupAt)}.` : 'First backup runs tonight.'}
          </p>
        </div>
        <div>
          {others.length > 0 ? (
            <button
              className="secondary"
              disabled={busy || !!active || d.state !== 'active'}
              onClick={() => setOpen(true)}
            >
              Move to another location
            </button>
          ) : (
            <button className="secondary" disabled title="More locations are coming soon">
              Move (more locations soon)
            </button>
          )}
        </div>
      </div>
      {others.length === 0 && regions.data && regions.data.planned.length > 0 && (
        <p className="muted" style={{ marginTop: 10, marginBottom: 0 }}>
          Coming soon: {regions.data.planned.map((r) => r.label).join(', ')}. When a location goes live you
          can move here with one click.
        </p>
      )}
      {active && (
        <div className="banner" style={{ marginTop: 14, marginBottom: 0 }}>
          Moving to <strong>{active.toRegionLabel}</strong>{' '}
          {active.status === 'running' ? '(copying data…)' : '(queued)'}. Your database keeps working while we
          copy it.{' '}
          {active.status === 'requested' && (
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                void cancel(active.id);
              }}
            >
              Cancel
            </a>
          )}
        </div>
      )}
      {!active && last && last.status !== 'cancelled' && (
        <p className="muted" style={{ marginTop: 10, marginBottom: 0 }}>
          Last move: {last.fromRegionLabel} → {last.toRegionLabel},{' '}
          {last.status === 'succeeded'
            ? `completed ${formatDate(last.finishedAt)}`
            : `failed: ${last.error ?? 'unknown error'}`}
          {last.status === 'succeeded' && (
            <>
              {' '}
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  onChanged();
                }}
              >
                refresh
              </a>
            </>
          )}
        </p>
      )}
      {open && (
        <Modal title={`Move ${d.name}`} onClose={() => setOpen(false)}>
          <p className="muted">
            Pick the new location. We copy the data live, then switch connections over in one short step.
            Nothing to change in your apps.
          </p>
          <div className="choice-grid">
            {others.map((r) => (
              <button
                key={r.id}
                type="button"
                className={`choice ${target === r.id ? 'active' : ''}`}
                onClick={() => setTarget(r.id)}
              >
                <strong>{r.label}</strong>
              </button>
            ))}
          </div>
          {error && <p className="error">{error}</p>}
          <div className="row">
            <button className="secondary" onClick={() => setOpen(false)}>
              Cancel
            </button>
            <button disabled={!target || busy} onClick={() => void submit()}>
              Move database
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
